import LocationOnIcon from '@mui/icons-material/LocationOn';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import MailOutlineOutlinedIcon from '@mui/icons-material/MailOutlineOutlined';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import WarehouseOutlined from '@mui/icons-material/WarehouseOutlined';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import { useTheme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import type { IWarehouse } from '@/types/fulfill/warehouse/warehouse.interface';

interface WarehouseCardIconProps {
  icon: React.ReactNode;
}

const WarehouseCardFieldIcon = (props: WarehouseCardIconProps) => {
  const theme = useTheme();

  return (
    <div
      className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg"
      style={{ background: theme.palette.primary.lighter, color: theme.palette.primary.darker }}
    >
      {props.icon}
    </div>
  );
};

interface WarehouseCardStatFieldProps {
  icon: React.ReactNode;
  label: string;
  value: string;
}

const WarehouseCardStatField = (props: WarehouseCardStatFieldProps) => {
  const theme = useTheme();

  return (
    <div className="flex flex-1 items-center gap-2.5">
      <WarehouseCardFieldIcon icon={props.icon} />
      <div className="min-w-0">
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            fontWeight: theme.typography.fontWeightBold,
            color: theme.palette.grey[500],
          }}
        >
          {props.label}
        </Typography>
        <Typography
          variant="body1"
          sx={{ fontWeight: theme.typography.fontWeightBold, color: theme.palette.primary.main }}
        >
          {props.value}
        </Typography>
      </div>
    </div>
  );
};

interface WarehouseCardTextFieldProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  action?: React.ReactNode;
}

const WarehouseCardTextField = (props: WarehouseCardTextFieldProps) => {
  const theme = useTheme();

  return (
    <div className="flex items-start justify-between gap-4">
      <div className="flex min-w-0 items-start gap-2.5">
        <WarehouseCardFieldIcon icon={props.icon} />
        <div className="min-w-0">
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              fontWeight: theme.typography.fontWeightBold,
              color: theme.palette.grey[500],
            }}
          >
            {props.label}
          </Typography>
          <Tooltip title={props.value}>
            <Typography variant="body2" noWrap sx={{ color: theme.palette.secondary.main }}>
              {props.value}
            </Typography>
          </Tooltip>
        </div>
      </div>
      {props.action && <div className="flex-shrink-0">{props.action}</div>}
    </div>
  );
};

interface WarehouseCardProps {
  warehouse: IWarehouse;
}

const WarehouseCard = (props: WarehouseCardProps) => {
  const { warehouse } = props;
  const theme = useTheme();

  const hasCoordinates = !!(warehouse.location.lat && warehouse.location.lng);

  const handleViewOnMap = () => {
    if (!hasCoordinates) return;
    window.open(
      `https://maps.google.com/?q=${warehouse.location.lat},${warehouse.location.lng}`,
      '_blank',
    );
  };

  return (
    <div
      className="flex h-full flex-col gap-4 rounded-3xl p-5"
      style={{ background: theme.palette.background.paper, boxShadow: theme.shadows[3] }}
    >
      <div className="flex items-center gap-4">
        <div
          className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg"
          style={{ background: theme.palette.primary.lighter, color: theme.palette.primary.darker }}
        >
          <WarehouseOutlined fontSize="small" />
        </div>
        <div className="min-w-0 flex-1">
          <Tooltip title={warehouse.name}>
            <Typography
              variant="subtitle2"
              noWrap
              sx={{
                fontWeight: theme.typography.fontWeightBold,
                color: theme.palette.secondary.main,
              }}
            >
              {warehouse.name}
            </Typography>
          </Tooltip>
        </div>
      </div>

      <Divider />

      <div className="flex flex-wrap items-center gap-2">
        <WarehouseCardStatField
          icon={<MailOutlineOutlinedIcon sx={{ fontSize: 16 }} />}
          label="Código postal"
          value={String(warehouse.location.zipcode)}
        />
        <Divider orientation="vertical" flexItem />
        <Tooltip title={hasCoordinates ? '' : 'Coordenadas no disponibles'}>
          <span>
            <Button
              variant="outlined"
              size="small"
              disabled={!hasCoordinates}
              onClick={handleViewOnMap}
              startIcon={<LocationOnIcon sx={{ fontSize: 16 }} />}
              sx={{ borderRadius: '999px', whiteSpace: 'nowrap', flexShrink: 0 }}
            >
              Ver en Google Maps
            </Button>
          </span>
        </Tooltip>
      </div>

      <Divider />

      <WarehouseCardTextField
        icon={<LocationOnOutlinedIcon sx={{ fontSize: 16 }} />}
        label="Dirección"
        value={warehouse.location.address}
      />

      <Divider />

      <WarehouseCardTextField
        icon={<MapOutlinedIcon sx={{ fontSize: 16 }} />}
        label="Ciudad y estado"
        value={`${warehouse.location.city}, ${warehouse.location.state}`}
      />
    </div>
  );
};

export default WarehouseCard;
